import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { dateFormatLocales } from '../../../pipes/datetime.pipe';
import { SHORT_LOCALE_ID } from '../../../shared/initialize.provider';
import { ThemeService } from '../../../theme/theme.service';
import Utils from '../../../utils/utils';
import { getPreference, Preference, removePreference } from '../../../utils/preferences.storage';
import { FormComponent, FORM_COMPONENT_TOKEN } from '../../form/form.component';

@Component({
  selector: 'app-date-lang-format',
  templateUrl: './date-lang-format.component.html',
  styleUrls: ['./date-lang-format.component.scss'],
  providers: [
    {
      provide: FORM_COMPONENT_TOKEN,
      useExisting: DateLangFormatComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateLangFormatComponent extends FormComponent<any> {
  now = DateTime.now();
  langOptions = ['English (GB)', 'Français'];
  localeOptions = [
    'Default language locale',
    dateFormatLocales.get('en-US').DISPLAY +
      `(${this.now.setLocale('en-US').toFormat(dateFormatLocales.get('en-US').DATETIME)})`,
    dateFormatLocales.get('en-GB').DISPLAY +
      `(${this.now.toFormat(dateFormatLocales.get('en-GB').DATETIME)})`,
    dateFormatLocales.get('fr-FR').DISPLAY +
      `(${this.now.toFormat(dateFormatLocales.get('fr-FR').DATETIME)})`,
    dateFormatLocales.get('en-ZA').DISPLAY +
      `(${this.now.toFormat(dateFormatLocales.get('en-ZA').DATETIME)})`,
  ];
  form = new FormGroup({
    lang: new FormControl<string>('', Validators.required),
    locale: new FormControl<string | null>('', Validators.required),
  });
  fields = [];
  service = null;

  constructor(
    private themeService: ThemeService,
    @Inject(SHORT_LOCALE_ID) public locale: string,
    private router: Router
  ) {
    super();
  }

  override patchFormWithEntity = (): void => {
    switch (this.locale) {
      case 'en':
        this.form.controls.lang.setValue('English (GB)');
        break;
      case 'fr':
        this.form.controls.lang.setValue('Français');
        break;
      default:
        break;
    }

    if (getPreference<string>(Preference.LOCALE)) {
      switch (Utils.getLocaleFormat(this.locale)) {
        case 'en-US':
          this.form.controls.locale.setValue(
            dateFormatLocales.get('en-US').DISPLAY +
              `(${this.now.setLocale('en-US').toFormat(dateFormatLocales.get('en-US').DATETIME)})`
          );
          break;
        case 'en-GB':
          this.form.controls.locale.setValue(
            dateFormatLocales.get('en-GB').DISPLAY +
              `(${this.now.toFormat(dateFormatLocales.get('en-GB').DATETIME)})`
          );
          break;
        case 'en-ZA':
          this.form.controls.locale.setValue(
            dateFormatLocales.get('en-ZA').DISPLAY +
              `(${this.now.toFormat(dateFormatLocales.get('en-ZA').DATETIME)})`
          );
          break;
        case 'fr-FR':
          this.form.controls.locale.setValue(
            dateFormatLocales.get('fr-FR').DISPLAY +
              `(${this.now.toFormat(dateFormatLocales.get('fr-FR').DATETIME)})`
          );
          break;
        default:
          break;
      }
    } else {
      this.form.controls.locale.setValue('Default language locale');
    }
  };

  override formToEntity = () => {
    let refresh = false;
    let newLang = 'en';
    let newLocale: string;

    switch (this.form.controls.lang.value) {
      case 'English (GB)':
        newLang = 'en';
        break;
      case 'Français':
        newLang = 'fr';
        break;
      default:
        break;
    }

    switch (this.form.controls.locale.value) {
      case dateFormatLocales.get('en-US').DISPLAY +
        `(${this.now.setLocale('en-US').toFormat(dateFormatLocales.get('en-US').DATETIME)})`:
        newLocale = 'en-US';
        break;
      case dateFormatLocales.get('en-GB').DISPLAY +
        `(${this.now.toFormat(dateFormatLocales.get('en-GB').DATETIME)})`:
        newLocale = 'en-GB';
        break;
      case dateFormatLocales.get('en-ZA').DISPLAY +
        `(${this.now.toFormat(dateFormatLocales.get('en-ZA').DATETIME)})`:
        newLocale = 'en-ZA';
        break;
      case dateFormatLocales.get('fr-FR').DISPLAY +
        `(${this.now.toFormat(dateFormatLocales.get('fr-FR').DATETIME)})`:
        newLocale = 'fr-FR';
        break;
      default:
        newLocale = '';
        break;
    }

    if (newLang !== this.locale || newLocale !== this.form.controls.locale.value) {
      refresh = true;
      this.locale = newLang;
      if (newLocale !== '' && newLocale !== null) {
        Utils.setDateLocaleFormat(newLocale);
        this.themeService.updateTheme();
      } else {
        removePreference(Preference.LOCALE);
      }
    }

    if (refresh) {
      window.location.assign(`../${this.locale}${this.router.url}`);
    }

    return true;
  };
}
